"use client";

import { Alert, AlertDescription, AlertTitle } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { toast } from "@shared/components/ui/snackbar";
import { IconRefresh } from "@tabler/icons-react";
import { useEffect, useEffectEvent } from "react";
import { useAuth } from "@/lib/auth";
import { formatBillingError } from "@/lib/billing/formatBillingError";
import { useBillingOverview } from "@/lib/billing/useBillingOverview";
import { useReconcileBilling } from "@/lib/billing/useReconcileBilling";
import { orgRoleCanManageBilling } from "@/lib/org/org-constants";
import { BillingOverviewTab, SpendLimitDialog } from "./billing.overview";
import { BillingInvoiceTab } from "./billing.tabs.invoice";
import { BillingPaymentTab } from "./billing.tabs.payment";

type BillingPageContentProps = {
  onLoadingChange?: (isLoading: boolean) => void;
};

const formatSpendLimit = (micro: string | null | undefined): string =>
  micro == null
    ? "Not set"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(Number(micro) / 1_000_000);

const formatSpendUsd = (micro: string | number | null | undefined): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(micro ?? 0) / 1_000_000);

const formatNextSpendResetDate = (): string => {
  const now = new Date();
  const nextReset = new Date(now.getFullYear(), now.getMonth() + 1, 1);

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(nextReset);
};

export const BillingPageContent = ({ onLoadingChange }: BillingPageContentProps) => {
  const { orgRole } = useAuth();
  const canManageBilling = orgRoleCanManageBilling(orgRole);
  const { data, error: billingError, isPending } = useBillingOverview();
  const { mutate: reconcile, isPending: isReconciling } = useReconcileBilling();

  const needsReconciliation = data?.billing_overview.needs_reconciliation ?? false;
  const overview = data?.billing_overview;
  const hasPaymentMethod = Boolean(overview?.billing_complete || (overview?.payment_methods.length ?? 0) > 0);

  const handleReconcile = () => {
    reconcile(
      {},
      {
        onSuccess: () => {
          toast.success("Billing reconciled", {
            description: "Your billing setup has been synced with the payment provider.",
          });
        },
        onError: (err) => {
          toast.error("Reconciliation failed", {
            description: err instanceof Error ? err.message : "Please try again or contact support.",
          });
        },
      },
    );
  };

  const handleLoadingChange = useEffectEvent((loading: boolean) => {
    onLoadingChange?.(loading);
  });

  useEffect(() => {
    handleLoadingChange(isPending);
  }, [isPending]);

  return (
    <div className="flex flex-col gap-8">
      <header className="space-y-1">
        <h2 className="text-3xl font-medium tracking-tight text-foreground">Billing</h2>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Your credits will be consumed with API usage. You can buy credits directly or set up auto-reload thresholds.
        </p>
      </header>

      {billingError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load billing</AlertTitle>
          <AlertDescription>{formatBillingError(billingError)}</AlertDescription>
        </Alert>
      ) : null}

      {needsReconciliation ? (
        <Alert>
          <IconRefresh className="size-4" aria-hidden />
          <AlertTitle>Billing setup needs attention</AlertTitle>
          <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>
              Your billing setup appears to be out of sync. This can happen when a payment method was added outside the
              normal setup flow or a background sync was missed.
            </span>
            {canManageBilling ? (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={handleReconcile}
                disabled={isReconciling}
              >
                {isReconciling ? "Reconciling…" : "Reconcile billing"}
              </Button>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      <BillingOverviewTab />

      {hasPaymentMethod ? (
        <section className="border border-border bg-card">
          <div className="flex flex-col gap-6 px-6 py-6">
            <div className="flex flex-col gap-1">
              <h3 className="text-base font-medium text-foreground">Spend limits</h3>
              <p className="text-sm text-muted-foreground">
                Cap organization API usage for the calendar month. Requests that would exceed the limit are blocked
                until you raise it or the month resets.
              </p>
            </div>

            {(() => {
              const usedMicro = Number(overview?.monthly_spend_used_micro ?? 0);
              const limitMicro =
                overview?.monthly_spend_limit_micro != null ? Number(overview.monthly_spend_limit_micro) : null;
              const pct =
                limitMicro != null && limitMicro > 0 ? Math.min(100, Math.round((usedMicro / limitMicro) * 100)) : 0;

              return (
                <div className="flex flex-col gap-2">
                  <div className="flex items-end justify-between gap-4 text-sm">
                    <div className="flex flex-col gap-0.5">
                      <p className="font-medium text-foreground">{formatSpendUsd(usedMicro)} spent</p>
                      <p className="text-xs text-muted-foreground">Resets {formatNextSpendResetDate()}</p>
                    </div>
                    <p className="shrink-0 text-sm text-muted-foreground">
                      {limitMicro != null ? `${pct}% used` : "No limit set"}
                    </p>
                  </div>
                  <div className="h-2 w-full bg-muted" aria-hidden>
                    <div className="h-full bg-chart-1" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })()}

            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-4 py-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">Monthly spend limit</p>
                  <p className="text-sm text-muted-foreground">
                    {formatSpendLimit(overview?.monthly_spend_limit_micro)}
                  </p>
                </div>
                {canManageBilling ? (
                  <SpendLimitDialog currentLimitMicro={overview?.monthly_spend_limit_micro} isLoading={isPending} />
                ) : null}
              </div>
              {/* TODO: Email spend-limit notifications are not available yet.
              <div className="flex items-center justify-between gap-4 border-t border-border py-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">Email notifications</p>
                  <p className="text-sm text-muted-foreground">
                    Get notified as your monthly spend approaches an amount you set.
                  </p>
                </div>
                <Button size="sm" variant="outline" disabled>
                  Add notifications
                </Button>
              </div>
              */}
            </div>
          </div>
        </section>
      ) : null}

      <BillingPaymentTab />
      <BillingInvoiceTab />
    </div>
  );
};
